import {
  getHouseQueryKey,
  useUpdateHouse
} from "../../API/queries/houseApiQueries";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { LoadingIndicator } from "../../components/LoadingIndicator";
import {
  Content,
  Footer,
  FooterButtons,
  Root,
  SectionContainer,
  Sections
} from "../ProfileUser/StudentProfileEdit/styles";
import {
  T_WIZARD_HOUSE_STEP,
  WIZARD_HOUSE_STEPS
} from "../../components/Wizard/interfaces";
import { IHouse, IHouseWithRentPeriod } from "../../interfaces/interfaces";
import { Button } from "antd";
import omit from "lodash/omit";
import { useHospiNavigate } from "../../utils/common";
import { HOST_HOUSE_EDIT_ROUTE, HOST_ROOMS_ROUTE } from "../../utils/constants";
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { SectionHeaderComponent } from "../ProfileUser/StudentProfileEdit";
import { useTranslation } from "react-i18next";
import { WizardStepSpaceTypeView } from "../../components/Wizard/House/Steps/WizardStepSpaceType";
import { WizardStepHouseLocationView } from "../../components/Wizard/House/Steps/WizardStepHouseLocation";
import { WizardStepAboutHouseHoldView } from "../../components/Wizard/House/Steps/WizardStepAboutHousehold";
import { WizardStepAboutSpaceView } from "../../components/Wizard/House/Steps/WizardStepAboutSpace";
import { WizardStepFacilitiesView } from "../../components/Wizard/House/Steps/WizardStepFacilities";
import { WizardStepUniquenessView } from "../../components/Wizard/House/Steps/WizardStepUniqueness";
import { WizardStepPhotosView } from "../../components/Wizard/House/Steps/WizardStepPhotos";
import { WizardStepAvailabilityView } from "../../components/Wizard/House/Steps/WizardStepAvailability";
import { WizardStepPricesView } from "../../components/Wizard/House/Steps/WizardStepPrices";
import { WizardStepIdealTenantView } from "../../components/Wizard/House/Steps/WizardStepIdealTenant";
import { WizardStepActivityExpectationsView } from "../../components/Wizard/House/Steps/WizardStepActivityExpectations";
import * as H from "history";
import {
  getChangedFields,
  getStepErrorsFromResponse,
  isReadonlyStepField
} from "../../components/Wizard/utils";
import { HouseAPI } from "../../API/houseApi";
import { useQueryClient } from "@tanstack/react-query";
import { usePromisesLoader } from "../../utils/usePromiseLoader";
import { API } from "aws-amplify";
import { scrollToFormWithError } from "../../utils/dom";

export const HouseEditPage = ({
  viewonly,
  canCancel = false
}: {
  viewonly: boolean;
  canCancel?: boolean;
}) => {
  const [house, setHouse] = useState<IHouseWithRentPeriod | null>(null);
  const { isLoading: isLoadingHouse, watchPromise: watchPromiseHouse } =
    usePromisesLoader();
  const params: { id?: string } = useParams();
  const navigate = useNavigate();
  const navigateHospi = useHospiNavigate();
  const { i18n } = useTranslation("house");
  const [editedHouse, setEditedHouse] = useState<IHouseWithRentPeriod | null>(
    null
  );
  const isInitialScrolled = useRef(false);
  const isPhotosChanged = useRef(false);
  const [errors, setErrors] = useState<
    Partial<{
      [K in keyof IHouse]: string;
    }>
  >({});
  const queryClient = useQueryClient();

  const rootRef = useRef<HTMLDivElement>(null);
  const stepsRefs = useRef<{
    [key in T_WIZARD_HOUSE_STEP]?: HTMLDivElement | null;
  }>({});
  const location: H.Location<{ stepName?: T_WIZARD_HOUSE_STEP } | null> =
    useLocation();

  const updateHouse = useUpdateHouse();

  const invalidateHouseQuery = useCallback(
    (id: string) => {
      queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(id)
      });
    },
    [queryClient]
  );

  useEffect(() => {
    if (!params.id) {
      return;
    }
    const promise = HouseAPI.getHouse(params.id);
    watchPromiseHouse(
      promise.then((h) => {
        setHouse(h);
      })
    );

    return () => {
      API.cancel(promise);
    };
  }, [params.id]);

  useEffect(() => {
    if (house) {
      setEditedHouse(house);
    }
  }, [house]);

  const handleEdit = useCallback(
    (_: React.MouseEvent<HTMLButtonElement>, stepName: T_WIZARD_HOUSE_STEP) => {
      navigate(
        generatePath(HOST_HOUSE_EDIT_ROUTE, {
          lng: i18n.language,
          id: house?.house_id!
        }),
        {
          state: {
            stepName: stepName,
            noNeedScrollTop: true
          }
        }
      );
    },
    [house?.house_id, navigate, i18n.language]
  );

  useEffect(() => {
    if (!editedHouse || isInitialScrolled.current) {
      return;
    }

    if (
      location.state?.stepName &&
      stepsRefs.current[location.state?.stepName]
    ) {
      setTimeout(() => {
        isInitialScrolled.current = true;
        if (location.state?.stepName) {
          stepsRefs.current?.[location.state?.stepName]?.scrollIntoView();
        }
      }, 500);
    }
  }, [location.state?.stepName, editedHouse]);

  const sendFromData = useCallback(() => {
    if (!editedHouse || !editedHouse.house_id || !house) {
      return;
    }
    updateHouse.mutate(
      {
        id: editedHouse.house_id,
        body: getChangedFields(
          omit(house, "photos"),
          omit(editedHouse, "photos")
        )
      },
      {
        onSuccess: () => {
          navigateHospi(HOST_ROOMS_ROUTE);
        },
        onError: (error: any) => {
          if (error?.response?.data?.message === "Could not unpack address") {
            setErrors({
              street: "Not valid"
            });
          } else {
            const formErrors = getStepErrorsFromResponse(error);
            if (formErrors) {
              setErrors(formErrors);
            }
          }
          scrollToFormWithError(rootRef.current!);
        }
      }
    );
  }, [editedHouse, house, i18n.language, navigate, updateHouse]);

  const readonly = updateHouse.isLoading;

  const handleCancel = useCallback(() => {
    if (isPhotosChanged.current) {
      invalidateHouseQuery(house?.house_id!);
    }
    navigateHospi(HOST_ROOMS_ROUTE);
  }, [house?.house_id, i18n.language, invalidateHouseQuery, navigate]);

  const handleImagesChanged = useCallback(() => {
    isPhotosChanged.current = true;
  }, []);

  return (
    <Root ref={rootRef}>
      <div>{isLoadingHouse ? <LoadingIndicator fontSize={30} /> : null}</div>
      {editedHouse ? (
        <HouseEditPageView
          stepsRefs={stepsRefs}
          values={editedHouse}
          viewonly={viewonly}
          isLoading={updateHouse.isLoading}
          errors={errors}
          readonly={readonly}
          canCancel={canCancel}
          setValues={setEditedHouse}
          onEdit={handleEdit}
          onCancel={handleCancel}
          onSubmit={sendFromData}
          onImagesChanged={handleImagesChanged}
        />
      ) : null}
    </Root>
  );
};

export const HouseEditPageView = ({
  values,
  setValues,
  errors,
  onCancel,
  onSubmit,
  isLoading,
  onImagesChanged,
  onEdit,
  stepsRefs,
  viewonly,
  readonly,
  canCancel,
  enabledStepsFields,
  submitButtonText
}: {
  onEdit?: (
    e: React.MouseEvent<HTMLButtonElement>,
    stepName: T_WIZARD_HOUSE_STEP
  ) => void;
  stepsRefs: MutableRefObject<{
    [key in T_WIZARD_HOUSE_STEP]?: HTMLDivElement | null;
  }>;
  viewonly: boolean;
  readonly: boolean;
  canCancel: boolean;
  isLoading: boolean;
  values: IHouse;
  setValues: React.Dispatch<React.SetStateAction<IHouseWithRentPeriod | null>>;
  errors: Partial<{
    [K in keyof IHouse]: string;
  }>;
  onCancel: () => void;
  onSubmit: () => void;
  submitButtonText?: string;
  onImagesChanged?: () => void;
  enabledStepsFields?: {
    [key in T_WIZARD_HOUSE_STEP]?: {
      [key: string]: boolean;
    };
  };
}) => {
  const { t } = useTranslation("house");
  const [emptyFields] = useState({});

  const getEnabledStepFields = (stepName: T_WIZARD_HOUSE_STEP) => {
    return enabledStepsFields === undefined
      ? undefined
      : enabledStepsFields[stepName] ?? emptyFields;
  };

  return (
    <Content>
      <Sections>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.SpaceType] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.SpaceType}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepSpaceTypeView
            values={useMemo(
              () => ({
                kind: values.kind
              }),
              [values.kind]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    kind: values.kind!
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.SpaceType)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.HouseLocation] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.HouseLocation}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepHouseLocationView
            values={useMemo(
              () => ({
                postal_code: values.postal_code,
                house_number: values.house_number,
                additional_house_number: values.additional_house_number,
                street: values.street,
                city: values.city,
                country: values.country
              }),
              [
                values.postal_code,
                values.house_number,
                values.additional_house_number,
                values.street,
                values.city,
                values.country
              ]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    postal_code: values.postal_code!,
                    house_number: values.house_number!,
                    additional_house_number: values.additional_house_number!,
                    street: values.street!,
                    city: values.city!,
                    country: values.country!
                  };
                });
              },
              [setValues]
            )}
            commonError={!!errors.street}
            errors={useMemo(() => ({}), [])}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(
              WIZARD_HOUSE_STEPS.HouseLocation
            )}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.AboutHousehold] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.AboutHousehold}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepAboutHouseHoldView
            values={useMemo(
              () => ({
                characteristics: values.characteristics,
                people_living: values.people_living,
                pets: values.pets,
                spoken_languages: values.spoken_languages,
                description: values.household_description
              }),
              [
                values.characteristics,
                values.people_living,
                values.household_description,
                values.pets,
                values.spoken_languages
              ]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    characteristics: values.characteristics,
                    people_living: values.people_living ?? null,
                    pets: values.pets ?? null,
                    spoken_languages: values.spoken_languages ?? [],
                    household_description: values.description ?? ""
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(
              WIZARD_HOUSE_STEPS.AboutHousehold
            )}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.AboutSpace] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.AboutSpace}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepAboutSpaceView
            values={useMemo(
              () => ({
                amount_of_people: values.amount_of_people,
                accommodation: values.accommodation,
                rent_area: values.bedroom_area,
                entire_house_area: values.house_area,
                shared_items: values.shared_items,
                private_items: values.private_items
              }),
              [
                values.accommodation,
                values.amount_of_people,
                values.bedroom_area,
                values.house_area,
                values.private_items,
                values.shared_items
              ]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    accommodation: values.accommodation,
                    bedroom_area: values.rent_area ?? null,
                    house_area: values.entire_house_area ?? null,
                    amount_of_people: values.amount_of_people,
                    private_items: values.private_items ?? [],
                    shared_items: values.shared_items ?? []
                  };
                });
              },
              [setValues]
            )}
            errors={useMemo(
              () => ({
                ...errors,
                rent_area: errors.bedroom_area,
                entire_house_area: errors.house_area
              }),
              [errors]
            )}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.AboutSpace)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.Facilities] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.Facilities}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepFacilitiesView
            values={useMemo(
              () => ({
                security_items: values.security_items,
                other_items_and_services: values.other_items_and_services
              }),
              [values.other_items_and_services, values.security_items]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    ...values
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.Facilities)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.Uniqueness] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.Uniqueness}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepUniquenessView
            values={useMemo(
              () => ({
                accommodation: values.uniqueness_accommodation,
                place_description: values.description
              }),
              [values.description, values.uniqueness_accommodation]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    uniqueness_accommodation: values.accommodation,
                    description: values.place_description ?? ""
                  };
                });
              },
              [setValues]
            )}
            errors={useMemo(
              () => ({
                ...errors,
                accommodation: errors.uniqueness_accommodation
              }),
              [errors]
            )}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.Uniqueness)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.Photos] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.Photos}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepPhotosView
            photos={useMemo(() => values.photos ?? [], [values.photos])}
            onImagesChanged={useCallback(
              (photos) => {
                onImagesChanged?.();
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    photos: photos.map((p) => ({
                      url: p.url!,
                      file_name: p.file_name,
                      order: p.order,
                      timestamp: p.timestamp!
                    }))
                  };
                });
              },
              [setValues, onImagesChanged]
            )}
            uploadPhoto={useCallback(
              (file, order) => {
                return HouseAPI.uploadPhoto(
                  values?.house_id!,
                  file,
                  Date.now(),
                  order
                );
              },
              [values?.house_id]
            )}
            deletePhoto={useCallback(
              (fileName) => {
                return HouseAPI.deletePhotos(
                  values?.house_id!,
                  [fileName].map((fileName) =>
                    fileName.replace("downloads/", "")
                  )
                );
              },
              [values?.house_id]
            )}
            reorderPhotos={useCallback(
              (order) => {
                return HouseAPI.reorderPhotos(values?.house_id!, order);
              },
              [values?.house_id]
            )}
            errors={errors.photos ?? null}
            readonly={isReadonlyStepField(
              "photos",
              readonly,
              getEnabledStepFields(WIZARD_HOUSE_STEPS.Photos)
            )}
            viewonly={viewonly}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.Availability] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.Availability}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepAvailabilityView
            values={useMemo(
              () => ({
                available_from: values.available_from,
                available_to: values.available_to
              }),
              [values.available_from, values.available_to]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    available_from: values.available_from!,
                    available_to: values.available_to!
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(
              WIZARD_HOUSE_STEPS.Availability
            )}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.Prices] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.Prices}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepPricesView
            values={useMemo(
              () => ({
                price: values.price,
                service_costs: values.service_costs,
                deposit: values.deposit,
                additional_expenses: values.additional_expenses
              }),
              [
                values.additional_expenses,
                values.deposit,
                values.price,
                values.service_costs
              ]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    ...values
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.Prices)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.IdealTenant] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.IdealTenant}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepIdealTenantView
            values={useMemo(
              () => ({
                min_age: values.min_tennant_age.toString(),
                max_age: values.max_tennant_age.toString(),
                gender: values.gender,
                tenant_spoken_languages:
                  Array.isArray(values.tenant_spoken_languages) &&
                  values.tenant_spoken_languages.length > 0
                    ? values.tenant_spoken_languages
                    : ["ANY"],
                tenant_characteristics: values.tenant_characteristics,
                tenant_description: values.tenant_description
              }),
              [
                values.gender,
                values.max_tennant_age,
                values.min_tennant_age,
                values.tenant_spoken_languages,
                values.tenant_characteristics,
                values.tenant_description
              ]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    min_tennant_age: parseInt(values.min_age! || "0"),
                    max_tennant_age: parseInt(values.max_age! || "0"),
                    gender: values.gender ?? null,
                    tenant_spoken_languages:
                      values.tenant_spoken_languages ?? [],
                    tenant_characteristics: values.tenant_characteristics,
                    tenant_description: values.tenant_description
                  };
                });
              },
              [setValues]
            )}
            errors={useMemo(
              () => ({
                ...errors,
                min_age: errors.min_tennant_age,
                max_age: errors.max_tennant_age
              }),
              [errors]
            )}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(WIZARD_HOUSE_STEPS.IdealTenant)}
          />
        </SectionContainer>
        <SectionContainer
          ref={useCallback(
            (el: HTMLDivElement | null) => {
              stepsRefs.current[WIZARD_HOUSE_STEPS.ActivityExpectations] = el;
            },
            [stepsRefs]
          )}
        >
          <SectionHeaderComponent
            stepName={WIZARD_HOUSE_STEPS.ActivityExpectations}
            onEdit={onEdit}
            showEditButton={viewonly}
            translationNamespace="house"
          />
          <WizardStepActivityExpectationsView
            values={useMemo(
              () => ({
                spend_time: values.spend_time,
                help_tenant: values.help_tenant
              }),
              [values.help_tenant, values.spend_time]
            )}
            setValues={useCallback(
              (values) => {
                setValues((prev) => {
                  if (!prev) {
                    return prev;
                  }

                  return {
                    ...prev,
                    ...values
                  };
                });
              },
              [setValues]
            )}
            errors={errors}
            readonly={readonly}
            viewonly={viewonly}
            enabledFields={getEnabledStepFields(
              WIZARD_HOUSE_STEPS.ActivityExpectations
            )}
          />
        </SectionContainer>
      </Sections>
      <Footer>
        {!viewonly ? (
          <FooterButtons>
            {canCancel ? (
              <Button size="large" onClick={onCancel}>
                {t("houseProfile.generic.buttonCancel.title")}
              </Button>
            ) : null}
            <Button
              size="large"
              onClick={onSubmit}
              type={"primary"}
              loading={isLoading}
            >
              {submitButtonText ?? t("houseProfile.generic.buttonSave.title")}
            </Button>
          </FooterButtons>
        ) : null}
      </Footer>
    </Content>
  );
};
